import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircle2, SendIcon, Camera, Smartphone, Dog, Gift, Percent, TrendingUp, BookOpen, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
export function CreatorProgram() {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    petType: '',
    socialHandle: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch('https://api.pawchi.app/api/creator-applications', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formState.name,
          email: formState.email,
          pet_kind: formState.petType,
          social_media_link: formState.socialHandle
        })
      });
      if (!response.ok) {
        throw new Error('Failed to submit application. Please try again.');
      }
      setSubmitted(true);
      setFormState({
        name: '',
        email: '',
        petType: '',
        socialHandle: ''
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  }
  return <div className="min-h-screen bg-cream" data-id="element-767">
      <div className="max-w-4xl mx-auto px-6 pt-32 pb-20" data-id="element-768">
        <Link to="/" className="inline-flex items-center gap-2 text-warmBrown/60 hover:text-peach transition-colors mb-8 font-medium" data-id="element-769">
          <ArrowLeftIcon size={18} data-id="element-770" />
          Back to Home
        </Link>

        <div className="grid md:grid-cols-2 gap-16" data-id="element-771">
          <div data-id="element-772">
            <h1 className="text-4xl md:text-5xl font-black text-warmBrown mb-4 leading-tight" data-id="element-773">
              Join the{' '}
              <span className="text-peach" data-id="element-774">Pawchi Creator Program</span>
            </h1>
            <p className="text-warmBrown/80 text-lg mb-8 leading-relaxed" data-id="element-775">
              Love sharing your pet online? Get early access to Pawchi,
              exclusive perks, and earn money by creating content featuring your
              furry friend. Whether you're an established creator or just have a
              pet with a great personality, we'd love to partner with you!
            </p>

            <div className="bg-white rounded-3xl p-8 shadow-soft mb-8 border border-warmBrown/5" data-id="element-776">
              <h3 className="font-bold text-xl text-warmBrown mb-6" data-id="element-777">
                Requirements
              </h3>
              <ul className="space-y-6" data-id="element-778">
                <li className="flex items-start gap-4" data-id="element-779">
                  <div className="w-10 h-10 rounded-full bg-peach/10 text-peach flex items-center justify-center flex-shrink-0" data-id="element-780">
                    <Dog size={20} data-id="element-781" />
                  </div>
                  <div data-id="element-782">
                    <p className="font-bold text-warmBrown" data-id="element-783">Own a dog or cat</p>
                    <p className="text-sm text-warmBrown/60" data-id="element-784">
                      Your pet is the star of the show.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4" data-id="element-785">
                  <div className="w-10 h-10 rounded-full bg-lavender/10 text-lavender flex items-center justify-center flex-shrink-0" data-id="element-786">
                    <Smartphone size={20} data-id="element-787" />
                  </div>
                  <div data-id="element-788">
                    <p className="font-bold text-warmBrown" data-id="element-789">Smartphone</p>
                    <p className="text-sm text-warmBrown/60" data-id="element-790">
                      To record content and use the Pawchi app.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4" data-id="element-791">
                  <div className="w-10 h-10 rounded-full bg-coral/10 text-coral flex items-center justify-center flex-shrink-0" data-id="element-792">
                    <Camera size={20} data-id="element-793" />
                  </div>
                  <div data-id="element-794">
                    <p className="font-bold text-warmBrown" data-id="element-795">
                      Willing to record videos
                    </p>
                    <p className="text-sm text-warmBrown/60" data-id="element-796">
                      Comfortable filming everyday moments.
                    </p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-soft mb-8 border border-warmBrown/5" data-id="element-797">
              <h3 className="font-bold text-xl text-warmBrown mb-6" data-id="element-798">Rewards</h3>
              <ul className="space-y-6" data-id="element-799">
                <li className="flex items-start gap-4" data-id="element-800">
                  <div className="w-10 h-10 rounded-full bg-sage/10 text-sage flex items-center justify-center flex-shrink-0" data-id="element-801">
                    <Gift size={20} data-id="element-802" />
                  </div>
                  <div data-id="element-803">
                    <p className="font-bold text-warmBrown" data-id="element-804">Free Pawchi Plus</p>
                    <p className="text-sm text-warmBrown/60" data-id="element-805">
                      Get full access to all premium features.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4" data-id="element-806">
                  <div className="w-10 h-10 rounded-full bg-peach/10 text-peach flex items-center justify-center flex-shrink-0" data-id="element-807">
                    <Percent size={20} data-id="element-808" />
                  </div>
                  <div data-id="element-809">
                    <p className="font-bold text-warmBrown" data-id="element-810">
                      Up to 20% affiliate commission!
                    </p>
                    <p className="text-sm text-warmBrown/60" data-id="element-811">
                      Earn money for every new user you bring in.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4" data-id="element-812">
                  <div className="w-10 h-10 rounded-full bg-lavender/10 text-lavender flex items-center justify-center flex-shrink-0" data-id="element-813">
                    <TrendingUp size={20} data-id="element-814" />
                  </div>
                  <div data-id="element-815">
                    <p className="font-bold text-warmBrown" data-id="element-816">
                      Bonuses for top-performing videos
                    </p>
                    <p className="text-sm text-warmBrown/60" data-id="element-817">
                      Extra rewards when your content goes viral.
                    </p>
                  </div>
                </li>
              </ul>
            </div>

            <Link to="/creator/help" className="group block bg-gradient-to-br from-peach to-coral rounded-3xl p-8 shadow-soft hover:shadow-soft-lg transition-shadow text-white" data-id="element-818">
              <div className="flex items-center gap-3 mb-3" data-id="element-819">
                <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center" data-id="element-820">
                  <BookOpen size={20} data-id="element-821" />
                </div>
                <p className="text-xs font-bold uppercase tracking-wider text-white/80" data-id="element-822">
                  Creator Resources
                </p>
              </div>
              <h3 className="font-black text-xl mb-2" data-id="element-823">Visit the Help Center</h3>
              <p className="text-white/90 text-sm leading-relaxed mb-4" data-id="element-824">
                Guides, protocols, and tips to help you grow your audience and
                earn more with Pawchi.
              </p>
              <span className="inline-flex items-center gap-2 font-bold text-sm group-hover:gap-3 transition-all" data-id="element-825">
                Browse guides
                <ArrowRight size={16} data-id="element-826" />
              </span>
            </Link>
          </div>

          <div className="relative" data-id="element-827">
            <div className="bg-white rounded-[2.5rem] p-8 md:p-10 shadow-soft-lg border border-warmBrown/5 sticky top-32" data-id="element-828">
              <h3 className="text-2xl font-bold text-warmBrown mb-2" data-id="element-829">
                Apply Now
              </h3>
              <p className="text-warmBrown/60 text-sm mb-8" data-id="element-830">
                Fill out the form below and our team will get back to you within
                48 hours.
              </p>

              {submitted ? <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="bg-sage/10 border border-sage/30 rounded-2xl p-6 text-center" data-id="element-831">
                  <div className="w-12 h-12 bg-sage/20 text-sage rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-832">
                    <CheckCircle2 size={24} data-id="element-833" />
                  </div>
                  <h4 className="font-bold text-warmBrown mb-2" data-id="element-834">
                    Application Received!
                  </h4>
                  <p className="text-sm text-warmBrown/70" data-id="element-835">
                    Thanks for applying to the Pawchi Creator Program. We'll be
                    in touch soon.
                  </p>
                  <button onClick={() => setSubmitted(false)} className="mt-6 text-sm font-bold text-peach hover:text-peach/80 transition-colors" data-id="element-836">
                    Submit another application
                  </button>
                </motion.div> : <form onSubmit={handleSubmit} className="space-y-5" data-id="element-837">
                  {error && <div className="p-3 bg-coral/10 border border-coral/20 rounded-xl text-coral text-sm" data-id="element-838">
                      {error}
                    </div>}

                  <div data-id="element-839">
                    <label className="block text-sm font-medium text-warmBrown/70 mb-2" data-id="element-840">
                      Your Name
                    </label>
                    <input type="text" required value={formState.name} onChange={e => setFormState({
                  ...formState,
                  name: e.target.value
                })} className="w-full px-4 py-3 rounded-xl bg-cream/50 border border-warmBrown/10 text-warmBrown focus:outline-none focus:border-peach/50 focus:ring-1 focus:ring-peach/30 transition-colors" placeholder="Jane Doe" data-id="element-841" />
                  </div>

                  <div data-id="element-842">
                    <label className="block text-sm font-medium text-warmBrown/70 mb-2" data-id="element-843">
                      Email Address
                    </label>
                    <input type="email" required value={formState.email} onChange={e => setFormState({
                  ...formState,
                  email: e.target.value
                })} className="w-full px-4 py-3 rounded-xl bg-cream/50 border border-warmBrown/10 text-warmBrown focus:outline-none focus:border-peach/50 focus:ring-1 focus:ring-peach/30 transition-colors" placeholder="jane@example.com" data-id="element-844" />
                  </div>

                  <div data-id="element-845">
                    <label className="block text-sm font-medium text-warmBrown/70 mb-2" data-id="element-846">
                      What kind of pet do you have?
                    </label>
                    <input type="text" required value={formState.petType} onChange={e => setFormState({
                  ...formState,
                  petType: e.target.value
                })} className="w-full px-4 py-3 rounded-xl bg-cream/50 border border-warmBrown/10 text-warmBrown focus:outline-none focus:border-peach/50 focus:ring-1 focus:ring-peach/30 transition-colors" placeholder="e.g. Golden Retriever, Calico Cat" data-id="element-847" />
                  </div>

                  <div data-id="element-848">
                    <label className="block text-sm font-medium text-warmBrown/70 mb-2" data-id="element-849">
                      Primary Social Media Link
                    </label>
                    <input type="url" required value={formState.socialHandle} onChange={e => setFormState({
                  ...formState,
                  socialHandle: e.target.value
                })} className="w-full px-4 py-3 rounded-xl bg-cream/50 border border-warmBrown/10 text-warmBrown focus:outline-none focus:border-peach/50 focus:ring-1 focus:ring-peach/30 transition-colors" placeholder="https://tiktok.com/@yourhandle" data-id="element-850" />
                  </div>

                  <button type="submit" disabled={isLoading} className="w-full mt-4 px-8 py-4 rounded-full font-bold text-white bg-peach hover:bg-peach/90 disabled:opacity-70 disabled:cursor-not-allowed transition-colors shadow-soft-lg flex items-center justify-center gap-2" data-id="element-851">
                    {isLoading ? 'Submitting...' : <>
                        Submit Application
                        <SendIcon size={18} data-id="element-852" />
                      </>}
                  </button>
                </form>}
            </div>
          </div>
        </div>
      </div>
    </div>;
}